import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_FIRMS_QUERY, CHECK_USER_FIRM_JOB_ASSIGNMENT_QUERY } from '../graphql/queries';
import { GET_USER_FIRMS_BY_USER_QUERY } from '../graphql/queries';
import { GET_ALL_USERS_QUERY } from '../graphql/queries/users';
import {
  ASSIGN_FIRM_TO_USER_MUTATION,
  DELETE_USER_FIRM_MUTATION,
} from '../graphql/mutations';

export const useUserFirms = (config = {}) => {
  const { userId } = config;
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();

  const [userFirms, setUserFirms] = useState([]);
  const [availableFirms, setAvailableFirms] = useState([]);
  const [targetUser, setTargetUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isRootUser = currentUser?.role === 'ROOT';

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await hasuraClient.query(GET_ALL_USERS_QUERY, {
        where: { id: { _eq: parseInt(userId) } },
      });
      setTargetUser((data.users ?? [])[0] ?? null);
    } catch {
      showNotification(t('userFirms.errors.fetchUser'), 'error');
    }
  }, [userId, t, showNotification]);

  const fetchUserFirms = useCallback(async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      const data = await hasuraClient.query(GET_USER_FIRMS_BY_USER_QUERY, {
        userId: parseInt(userId),
      });
      const transformed = (data.user_firms ?? []).map(uf => ({
        id: uf.id,
        user_id: uf.user_id,
        firm_id: uf.firm_id,
        firm_name: uf.firm?.firm_name || '',
        firm_short_name: uf.firm?.firm_short_name || '',
        firm_city: uf.firm?.firm_city || '',
      }));
      setUserFirms(transformed);
    } catch {
      showNotification(t('userFirms.errors.fetchAssigned'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userId, t, showNotification]);

  const fetchAvailableFirms = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_FIRMS_QUERY, {
        where: { company_id: { _eq: currentUser?.companyId } },
      });
      setAvailableFirms(data.firms ?? []);
    } catch {
      showNotification(t('userFirms.errors.fetchFirms'), 'error');
    }
  }, [currentUser?.companyId, t, showNotification]);

  const assignFirmToUser = useCallback(async formData => {
    const firmIds = Array.isArray(formData.firmIds) ? formData.firmIds : [formData.firmIds];
    if (!firmIds.length) return false;

    try {
      setIsCreating(true);
      await Promise.all(
        firmIds.map(firmId =>
          hasuraClient.mutate(ASSIGN_FIRM_TO_USER_MUTATION, {
            userId: parseInt(userId),
            firmId: parseInt(firmId),
            companyId: currentUser?.companyId,
          })
        )
      );
      showNotification(t('userFirms.assignSuccess'), 'success');
      await fetchUserFirms();
      await fetchAvailableFirms();
      return true;
    } catch {
      showNotification(t('userFirms.assignError'), 'error');
      return false;
    } finally {
      setIsCreating(false);
    }
  }, [userId, currentUser?.companyId, fetchUserFirms, fetchAvailableFirms, t, showNotification]);

  const deleteUserFirms = useCallback(async rowId => {
    const row = userFirms.find(uf => uf.id === parseInt(rowId));
    const firmId = row?.firm_id;

    if (firmId) {
      try {
        const data = await hasuraClient.query(CHECK_USER_FIRM_JOB_ASSIGNMENT_QUERY, {
          userId: parseInt(userId),
          firmId: parseInt(firmId),
        });
        if ((data.job_assignments ?? []).length > 0) {
          showNotification(t('userFirms.errors.hasJobAssignment'), 'warning');
          return false;
        }
      } catch {
        // proceed with deletion if check fails
      }
    }

    try {
      setIsDeleting(true);
      await hasuraClient.mutate(DELETE_USER_FIRM_MUTATION, {
        id: parseInt(rowId),
      });
      showNotification(t('userFirms.removeSuccess'), 'success');
      await fetchUserFirms();
      await fetchAvailableFirms();
      return true;
    } catch {
      showNotification(t('userFirms.removeError'), 'error');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [userFirms, userId, fetchUserFirms, fetchAvailableFirms, t, showNotification]);

  const handleFormSubmit = useCallback(async formData => {
    return await assignFirmToUser(formData);
  }, [assignFirmToUser]);

  useEffect(() => {
    if (userId && currentUser?.id) {
      fetchUser();
      fetchUserFirms();
      fetchAvailableFirms();
    }
  }, [userId, currentUser?.id, fetchUser, fetchUserFirms, fetchAvailableFirms]);

  return {
    userFirms,
    availableFirms,
    targetUser,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isRootUser,
    handleFormSubmit,
    deleteUserFirms,
    additionalData: {
      availableFirms,
      targetUser,
      userFirms,
    },
  };
};
